import type { Role } from '@/types/domain';

// RBAC capability matrix (Constitución: Menor privilegio).
// This mirrors — but does NOT replace — Postgres Row Level Security.
// RLS is the source of truth; this gates UI affordances for a clean UX.

export type Capability =
  | 'clients.read'
  | 'clients.write'
  | 'documents.read'
  | 'documents.write'
  | 'documents.download'
  | 'tax_returns.read'
  | 'tax_returns.write'
  | 'portal.manage'
  | 'notarization.request'
  | 'notarization.sign'
  | 'payments.read'
  | 'payments.manage'
  | 'subscriptions.manage'
  | 'ai.use'
  | 'settings.manage'
  | 'security.manage'
  | 'audit.read'
  | 'reports.read'
  | 'sandbox.use'
  | 'team.manage'
  | 'readiness.override'
  | 'tasks.use'
  | 'messages.use'
  | 'crm.use'
  // Turnos de oficina (0070): recepción y escritorios — todo el staff.
  | 'queue.use';

const ALL: Capability[] = [
  'clients.read', 'clients.write', 'documents.read', 'documents.write',
  'documents.download', 'tax_returns.read', 'tax_returns.write', 'portal.manage',
  'notarization.request', 'notarization.sign', 'payments.read', 'payments.manage',
  'subscriptions.manage', 'ai.use', 'settings.manage', 'security.manage',
  'audit.read', 'reports.read', 'sandbox.use', 'team.manage',
  // §8.8: saltar una compuerta de readiness es un acto de AUTORIDAD (solo
  // admin/super_admin) — el preparador pide, no se auto-autoriza.
  'readiness.override',
  'tasks.use',
  'messages.use',
  'crm.use',
  'queue.use',
];

export const ROLE_CAPABILITIES: Record<Role, Capability[]> = {
  super_admin: ALL,
  admin: ALL.filter((c) => c !== 'security.manage' || true), // admin: full ops, security shared
  tax_preparer: [
    'clients.read', 'clients.write', 'documents.read', 'documents.write',
    'documents.download', 'tax_returns.read', 'tax_returns.write', 'portal.manage',
    'ai.use', 'reports.read', 'sandbox.use', 'tasks.use', 'messages.use', 'crm.use', 'queue.use',
  ],
  notary: [
    'documents.read', 'notarization.sign', 'payments.read', 'ai.use', 'sandbox.use', 'tasks.use',
    // Videollamada de identidad (0038): el enlace viaja por el hilo seguro.
    'messages.use', 'queue.use',
  ],
  client: [
    'documents.read', 'documents.write', 'tax_returns.read',
    // «Mis recibos» (12-sep): la RLS ya limita payments a payer_profile_id =
    // auth.uid(); la UI se adapta por rol (PaymentsPage sin consola de firma).
    'notarization.request', 'payments.read', 'ai.use', 'messages.use',
  ],
  support: [
    'clients.read', 'documents.read', 'tax_returns.read', 'ai.use', 'reports.read', 'tasks.use', 'messages.use', 'crm.use', 'queue.use',
  ],
  auditor: [
    'clients.read', 'documents.read', 'tax_returns.read', 'payments.read',
    'audit.read', 'reports.read',
  ],
};

export function can(role: Role | undefined, capability: Capability): boolean {
  if (!role) return false;
  return ROLE_CAPABILITIES[role]?.includes(capability) ?? false;
}
